import React from "react";
import { createColumnHelper } from "@tanstack/react-table";
import dayjs from "dayjs";
import { Box, Stack, Typography } from "@mui/material";
import {
  getLicenseTransactionTypeLabel,
  LICENSE_TRANSACTION_TYPE,
  normalizeLicenseTransactionType,
  ORG_TRANSACTION_TYPE_OPTIONS,
} from "const/license";

const columnHelper = createColumnHelper();

export const defaultTabs = [
  { name: "All", value: "all", isActive: true, tabCount: 0 },
  ...ORG_TRANSACTION_TYPE_OPTIONS.map((type) => ({
    ...type,
    isActive: false,
    tabCount: 0,
  })),
];

export const formatPeriod = (period) => {
  if (!period) return undefined;
  if (Array.isArray(period)) {
    return period.length > 1
      ? `${period[0]} → ${period[period.length - 1]}`
      : period[0];
  }
  return period;
};

export const maskLicenseKey = (key) => {
  if (!key) return "-";
  const parts = String(key).split("-");
  if (parts.length < 3) return "••••••";
  return parts
    .map((part, index) =>
      index === 0 || index === parts.length - 1 ? part : "•••••",
    )
    .join("-");
};

const formatTransactionTime = (timestamp) => {
  if (timestamp == null) return undefined;
  const timestampMs = Number(timestamp) * 1000;
  if (!Number.isFinite(timestampMs)) return undefined;
  const date = new Date(timestampMs);
  if (Number.isNaN(date.getTime())) return undefined;
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);
  const value = Object.fromEntries(
    parts.map(({ type, value }) => [type, value]),
  );
  return `${value.year}-${value.month}-${value.day} ${value.hour}:${value.minute}:${value.second} UTC+8`;
};

export const getDescriptionItems = (
  tx,
  { mspName, walletName, walletType } = {},
) => {
  const type = normalizeLicenseTransactionType(tx.type);
  const p = tx.descriptionParams || {};

  const getParam = (key) => {
    const lKey = key.toLowerCase();
    const actualKey = Object.keys(p).find((k) => k.toLowerCase() === lKey);
    return actualKey ? p[actualKey] : undefined;
  };

  const wallet = () => {
    if (walletName && walletType) {
      return `${walletName} (${walletType === "MSP" ? "MSP wallet" : "Org wallet"})`;
    }
    const explicit = getParam("wallet");
    if (explicit) return explicit;
    const org = getParam("organization") || getParam("orgName");
    const msp = getParam("mspName") || getParam("msp");
    if (org && msp) return `${org} (Org wallet) | ${msp} (MSP wallet)`;
    if (org) return `${org} (Org wallet)`;
    if (msp) return `${msp} (MSP wallet)`;
    return undefined;
  };
  const time = () =>
    tx.timestamp != null
      ? formatTransactionTime(tx.timestamp)
      : getParam("time");
  const actor = () => getParam("actor") || getParam("account");
  const period = () => formatPeriod(getParam("period"));
  const rowsForSiteService = () => [
    ["Wallet", wallet()],
    ["Site", getParam("site")],
    ["Service", getParam("service")],
    ["Device", getParam("device") ?? getParam("devices")],
    ["Rate", getParam("rate")],
  ];
  let rows = [];

  switch (type) {
    case LICENSE_TRANSACTION_TYPE.IMPORT:
      rows = [
        ["Actor", actor()],
        ["License Key", maskLicenseKey(getParam("key"))],
        ["Txn ID", getParam("txnId")],
        ["Time", time()],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.SUBSCRIBE:
    case LICENSE_TRANSACTION_TYPE.MONTHLY_CHARGE:
    case LICENSE_TRANSACTION_TYPE.ADDED_DEVICE:
    case LICENSE_TRANSACTION_TYPE.REMOVE_DEVICE:
    case LICENSE_TRANSACTION_TYPE.UNSUBSCRIBE: {
      const days = getParam("days");
      const returned =
        [
          LICENSE_TRANSACTION_TYPE.REMOVE_DEVICE,
          LICENSE_TRANSACTION_TYPE.UNSUBSCRIBE,
        ].includes(type) && days !== undefined
          ? `${days} (unused, returned)`
          : days;

      const rawDevice = getParam("device") ?? getParam("devices");
      const totalDevice = getParam("totalDevice") ?? getParam("totalDevices");
      let deviceValue = rawDevice;

      if (rawDevice !== undefined && rawDevice !== null && rawDevice !== "") {
        if (type === LICENSE_TRANSACTION_TYPE.ADDED_DEVICE) {
          const num = Number(rawDevice);
          const countStr = !isNaN(num)
            ? `+${Math.abs(num)}`
            : String(rawDevice).startsWith("+")
              ? rawDevice
              : `+${rawDevice}`;
          deviceValue =
            totalDevice !== undefined &&
            totalDevice !== null &&
            totalDevice !== ""
              ? `${countStr} (site total ${totalDevice})`
              : countStr;
        } else if (type === LICENSE_TRANSACTION_TYPE.REMOVE_DEVICE) {
          const num = Number(rawDevice);
          const countStr = !isNaN(num)
            ? `−${Math.abs(num)}`
            : String(rawDevice).startsWith("-") ||
                String(rawDevice).startsWith("−")
              ? `−${String(rawDevice).replace(/^[-−]/, "")}`
              : `−${rawDevice}`;
          deviceValue =
            totalDevice !== undefined &&
            totalDevice !== null &&
            totalDevice !== ""
              ? `${countStr} (site total ${totalDevice})`
              : countStr;
        }
      }

      rows = [
        ["Site", getParam("site")],
        ["Service", getParam("service")],
        ["Device", deviceValue],
        ["Rate", getParam("rate")],
        ["Days", returned ?? getParam("days")],
        ["Period", period()],
        ["Actor", actor()],
        ["Txn ID", getParam("txnId")],
        ["Time", time()],
      ];
      break;
    }
    case LICENSE_TRANSACTION_TYPE.PURCHASE:
      rows = [
        ["Wallet", wallet()],
        ["Account", getParam("account")],
        ["Order Ref.", getParam("orderReference")],
        ["Granted Credits", getParam("grantedCredits")],
        ["Actor", actor()],
        ["Txn ID", getParam("txnId")],
        ["Time", time()],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.REVERSAL:
      rows = [
        ["Wallet", wallet()],
        ["Order Ref.", getParam("orderReference")],
        ["Original Grant", getParam("originalGrant")],
        ["This Reversal", getParam("thisReversal")],
        ["Cumulative Reversed", getParam("cumulativeReversed")],
        ["Reclaimed in Full", getParam("reclaimedInFull")],
        ["Actor", actor()],
        ["Txn ID", getParam("txnId")],
        ["Time", time()],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.COVER_CURRENT_MONTH:
      rows = getParam("site")
        ? [
            ...rowsForSiteService(),
            ["Days", getParam("days")],
            ["Period", period()],
            ["Settles", getParam("settleTxnId")],
            ["Actor", actor()],
            ["Txn ID", getParam("txnId")],
            ["Time", time()],
          ]
        : [
            ["Wallet", mspName ? `${mspName} (MSP wallet)` : wallet()],
            ["Scope", "MSP layer — no site, no device count"],
            ["Service", getParam("service")],
            ["Rate", getParam("rate")],
            ["Settles", getParam("settles") || getParam("settleTxnId")],
            ["Actor", actor()],
            ["Txn ID", getParam("txnId")],
            ["Time", time()],
          ];
      break;
    case LICENSE_TRANSACTION_TYPE.PARTIAL_GRANT: {
      const daysGranted = getParam("daysGranted");
      const daysTotal = getParam("daysTotal");
      const daysGrantedValue =
        daysGranted !== undefined && daysTotal !== undefined
          ? `${daysGranted} of ${daysTotal}`
          : (daysGranted ?? (daysTotal ? `- of ${daysTotal}` : undefined));

      rows = [
        ["Site", getParam("site"), false],
        [
          "Scope",
          getParam("site") ? undefined : "MSP layer — no site, no device count",
          false,
        ],
        ["Wallet", wallet()],
        ["Service", getParam("service")],
        ["Device", getParam("device"), false],
        ["Rate", getParam("rate")],
        ["Days granted", daysGrantedValue],
        ["Days Unbilled", getParam("daysUnbilled")],
        ["Period", period()],
        ["Actor", actor()],
        ["Txn ID", getParam("txnId")],
        ["Time", time()],
      ];
      break;
    }
    case LICENSE_TRANSACTION_TYPE.MSP_DISBURSEMENT:
    case LICENSE_TRANSACTION_TYPE.MSP_WITHDRAWAL: {
      const msp = getParam("mspName") || getParam("msp");
      const org = getParam("organization") || getParam("orgName");
      const isDisbursement = type === LICENSE_TRANSACTION_TYPE.MSP_DISBURSEMENT;
      const fromMsp =
        getParam("fromMsp") || (isDisbursement && (getParam("from") || msp));
      const fromOrg =
        getParam("fromOrg") || (!isDisbursement && (getParam("from") || org));
      const toMsp =
        getParam("toMsp") || (!isDisbursement && (getParam("to") || msp));
      const toOrg =
        getParam("toOrg") || (isDisbursement && (getParam("to") || org));

      const formatWallet = (val, suffix) => {
        if (!val) return undefined;
        return val.includes(suffix) ? val : `${val} ${suffix}`;
      };

      rows = [
        [
          "From wallet",
          formatWallet(fromMsp, "(MSP wallet / treasury)"),
          false,
        ],
        ["From wallet", formatWallet(fromOrg, "(Org wallet)"), false],
        ["To wallet", formatWallet(toMsp, "(MSP wallet / treasury)"), false],
        ["To wallet", formatWallet(toOrg, "(Org wallet)"), false],
        [
          isDisbursement ? "From" : "To",
          getParam(isDisbursement ? "from" : "to"),
          false,
        ],
        ["Actor", actor()],
        ["MSP-side Txn ID", getParam("mspSideTxnId"), false],
        ["Txn ID", getParam("txnId")],
        ["Time", time()],
      ];
      break;
    }
    case LICENSE_TRANSACTION_TYPE.COMPENSATORY_KEY_ISSUED:
      rows = [
        ["Wallet", wallet()],
        [
          "Scope",
          getParam("scope") || "the wallet being torn down — an Org OR an MSP",
        ],
        ["License Key", maskLicenseKey(getParam("key"))],
        ["Key Value", getParam("keyValue")],
        ["Sent To", getParam("sentTo")],
        ["Reason", getParam("reason")],
        ["Actor", actor()],
        ["Txn ID", getParam("txnId")],
        ["Time", time()],
      ];
      break;
    case LICENSE_TRANSACTION_TYPE.MANUAL_ADJUSTMENT:
      rows = [
        ["Wallet", wallet()],
        ["Operator", getParam("operator")],
        ["Approved By", getParam("approvedBy")],
        ["Tier", getParam("tier")],
        ["Reason", getParam("reason")],
        ["Txn ID", getParam("txnId")],
        ["Time", time()],
      ];
      break;
    default:
      return [];
  }

  return rows
    .filter(
      ([, value, isRequired = true]) =>
        isRequired || (value !== null && value !== undefined && value !== ""),
    )
    .map(([label, value]) => ({ label, value: value ?? "-" }));
};

export const getDescriptionText = (tx, separator = " | ", options) => {
  const items = getDescriptionItems(tx, options);
  if (!items || items.length === 0) {
    return tx.description || "-";
  }
  return items.map(({ label, value }) => `${label}: ${value}`).join(separator);
};

export const getDescription = (tx, options) => {
  const items = getDescriptionItems(tx, options);
  if (!items || items.length === 0) return "-";

  return (
    <Stack spacing={0}>
      {items.map(({ label, value }) => (
        <Box key={label}>
          <Typography
            variant='body2'
            component='span'
            sx={{ color: "text.secondary", fontSize: "13px" }}
          >
            {label}:{" "}
          </Typography>
          <Typography
            variant='body2'
            component='span'
            sx={{ fontWeight: "bold", fontSize: "13px" }}
          >
            {value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

export const getTransactionsTableColumns = ({
  theme,
  walletName,
  walletType,
}) => [
  columnHelper.accessor(
    (row) => dayjs(row.timestamp).format("YYYY-MM-DD HH:mm:ss"),
    {
      id: "formattedDate",
      header: "Date",
      sortingFn: (rowA, rowB) => {
        return rowA.original.timestamp - rowB.original.timestamp;
      },
      cell: (info) => (
        <Typography sx={{ fontSize: "13px", color: "#111" }}>
          {info.getValue()}
        </Typography>
      ),
    },
  ),
  columnHelper.accessor("type", {
    header: "Type",
    cell: (info) => (
      <Typography sx={{ fontSize: "13px", color: "#111" }}>
        {getLicenseTransactionTypeLabel(info.getValue())}
      </Typography>
    ),
  }),
  columnHelper.accessor("change", {
    header: "Amount",
    sortingFn: (rowA, rowB, columnId) => {
      const a = Number(rowA.getValue(columnId)) || 0;
      const b = Number(rowB.getValue(columnId)) || 0;
      return a - b;
    },
    cell: (info) => {
      const value = info.getValue();
      const isPositive = value > 0;
      return (
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "13px",
            color: isPositive ? "#2E7D32" : "#000000",
          }}
        >
          {isPositive ? `+${value.toLocaleString()}` : value.toLocaleString()}
        </Typography>
      );
    },
  }),
  columnHelper.accessor(
    (row) => getDescriptionText(row, " | ", { walletName, walletType }),
    {
      id: "description",
      header: "Description",
      cell: (info) =>
        getDescription(info.row.original, { walletName, walletType }),
    },
  ),
  columnHelper.accessor("balance", {
    header: "Balance After",
    sortingFn: (rowA, rowB, columnId) => {
      const a = Number(rowA.getValue(columnId)) || 0;
      const b = Number(rowB.getValue(columnId)) || 0;
      return a - b;
    },
    cell: (info) => (
      <Typography sx={{ fontSize: "13px", color: "#111" }}>
        {info.getValue()?.toLocaleString()}
      </Typography>
    ),
  }),
];
